/**
 * Attribution Comparison component.
 *
 * Every number comes from GET /api/revenue/attribution/compare (per-model,
 * per-channel credited revenue over the period's real journeys) and
 * GET /api/revenue/metrics/channels (per-channel spend). Nothing is mocked:
 * a period with no conversions renders an empty state, not zeros.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  api,
  ApiFailure,
  type AttributionComparisonData,
  type AttributionModelName,
  type ChannelBreakdown,
} from '../api';
import { Notice } from './Panels';

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
}

const MODEL_LABELS: Record<AttributionModelName, string> = {
  first_touch: 'First Touch',
  last_touch: 'Last Touch',
  linear: 'Linear',
  time_decay: 'Time Decay',
  position_based: 'Position-Based',
  markov: 'Markov Chains',
  shapley: 'Shapley Value',
};

const MODEL_ORDER = Object.keys(MODEL_LABELS) as AttributionModelName[];

/** Compare-all is the default; picking a tab drills into one model. */
type ModelSelection = AttributionModelName | 'all';

function periodDates(dateRange: string): { startDate: string; endDate: string } {
  const endDate = new Date().toISOString().slice(0, 10);
  const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : dateRange === '90d' ? 90 : 365;
  const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  return { startDate, endDate };
}

export function AttributionComparison() {
  const [dateRange, setDateRange] = useState('30d');
  const [selection, setSelection] = useState<ModelSelection>('all');
  const [comparison, setComparison] = useState<AttributionComparisonData | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [spendByChannel, setSpendByChannel] = useState<Map<string, number>>(new Map());
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState<ApiFailure | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFailure(null);
    try {
      const { startDate, endDate } = periodDates(dateRange);
      const [comparisonData, channelsData] = await Promise.all([
        api.compareAttribution({ startDate, endDate }),
        api.getRevenueChannelBreakdown({ startDate, endDate }),
      ]);
      setComparison(comparisonData.comparison);
      setWarning(comparisonData.warning);
      setSpendByChannel(new Map(channelsData.breakdown.map((row: ChannelBreakdown) => [row.channel, row.spend])));
    } catch (error) {
      setComparison(null);
      setFailure(
        error instanceof ApiFailure ? error : new ApiFailure('Request failed.', 'Try again.', 0),
      );
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => { void load(); }, [load]);

  return (
    <div className="page">
      <header className="intro">
        <h2>Attribution Model Comparison</h2>
        <p>Compare how different attribution models distribute revenue credit across channels.</p>
      </header>

      <div className="controls">
        <label className="field">
          <span>Period</span>
          <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="12m">Last 12 months</option>
          </select>
        </label>
      </div>

      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}

      {loading && failure === null && (
        <div className="card"><p className="muted"><span className="spinner" /> Comparing attribution models…</p></div>
      )}

      {!loading && failure === null && comparison !== null && comparison.journeysAnalyzed === 0 && (
        <div className="card empty">
          <h3>No conversions in this period</h3>
          <p>
            Report revenue via the payment API, wait for tracked conversions, or import sales history
            from an uploaded CSV on the Revenue page
            {comparison.journeysSkipped > 0
              ? ` — ${comparison.journeysSkipped} conversion(s) had no tracked touchpoints and could not be attributed.`
              : '.'}
          </p>
        </div>
      )}

      {!loading && failure === null && comparison !== null && comparison.journeysAnalyzed > 0 && (
        <>
          {warning !== null && <Notice message={warning} remedy="Collect more journeys before trusting the algorithmic models' shares." />}

          <div className="segmented" style={{ marginBottom: 14, flexWrap: 'wrap' }}>
            <button aria-pressed={selection === 'all'} onClick={() => setSelection('all')}>
              Compare all
            </button>
            {MODEL_ORDER.map((model) => (
              <button key={model} aria-pressed={selection === model} onClick={() => setSelection(model)}>
                {MODEL_LABELS[model]}
              </button>
            ))}
          </div>

          {selection === 'all'
            ? <ModelHeatStrip comparison={comparison} />
            : <ModelDetail model={selection} comparison={comparison} spendByChannel={spendByChannel} />}
        </>
      )}
    </div>
  );
}

/**
 * One model in detail: per-channel credit, spend from the channel breakdown,
 * and the delta against last-touch — the model most teams already know.
 */
function ModelDetail({
  model,
  comparison,
  spendByChannel,
}: {
  model: AttributionModelName;
  comparison: AttributionComparisonData;
  spendByChannel: Map<string, number>;
}) {
  const summary = comparison.models.find((m) => m.model === model);
  const lastTouch = comparison.models.find((m) => m.model === 'last_touch');
  if (summary === undefined) return null;

  // Channels with spend but no credit still earn a row — zero credit against
  // real spend is a fact, not an absence.
  const channelNames = [...new Set([...summary.channels.map((c) => c.channel), ...spendByChannel.keys()])];
  const lastTouchRevenue = new Map((lastTouch?.channels ?? []).map((c) => [c.channel, c.revenue]));

  return (
    <section className="card">
      <header>
        <h2>{MODEL_LABELS[model]}</h2>
        <span className="sub">
          {summary.conversions} conversions · {formatCurrency(summary.attributedRevenue)} attributed
        </span>
      </header>
      <div style={{ overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Channel</th>
              <th style={{ textAlign: 'right' }}>Credit</th>
              <th style={{ textAlign: 'right' }}>Attributed</th>
              <th style={{ textAlign: 'right' }}>Spend</th>
              <th style={{ textAlign: 'right' }}>ROAS</th>
              <th style={{ textAlign: 'right' }}>CAC</th>
              {model !== 'last_touch' && <th style={{ textAlign: 'right' }}>Δ vs Last Touch</th>}
            </tr>
          </thead>
          <tbody>
            {channelNames.map((channel) => {
              const row = summary.channels.find((c) => c.channel === channel);
              const spend = spendByChannel.get(channel) ?? 0;
              const revenue = row?.revenue ?? 0;
              const conversions = row?.conversions ?? 0;
              const delta = revenue - (lastTouchRevenue.get(channel) ?? 0);
              return (
                <tr key={channel}>
                  <td style={{ fontWeight: 500 }}>{channel}</td>
                  <td style={{ textAlign: 'right' }}>{row !== undefined ? `${(row.creditShare * 100).toFixed(1)}%` : '0.0%'}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(revenue)}</td>
                  <td style={{ textAlign: 'right' }}>{spend > 0 ? formatCurrency(spend) : '—'}</td>
                  <td style={{ textAlign: 'right' }}>{spend > 0 ? `${(revenue / spend).toFixed(2)}x` : '—'}</td>
                  <td style={{ textAlign: 'right' }}>{spend > 0 && conversions > 0 ? formatCurrency(spend / conversions) : '—'}</td>
                  {model !== 'last_touch' && (
                    <td style={{ textAlign: 'right' }} className={delta > 0 ? 'delta up' : delta < 0 ? 'delta down' : undefined}>
                      {delta === 0 ? '—' : `${delta > 0 ? '+' : ''}${formatCurrency(delta)}`}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * Channel × model credit-share matrix. Cell intensity scales with the share
 * against the largest share in the matrix, so color is comparable everywhere.
 */
function ModelHeatStrip({ comparison }: { comparison: AttributionComparisonData }) {
  const models = MODEL_ORDER
    .map((model) => comparison.models.find((m) => m.model === model))
    .filter((m): m is NonNullable<typeof m> => m !== undefined);

  const channelNames = [...new Set(models.flatMap((m) => m.channels.map((c) => c.channel)))]
    .sort((a, b) => {
      const total = (name: string) =>
        models.reduce((sum, m) => sum + (m.channels.find((c) => c.channel === name)?.creditShare ?? 0), 0);
      return total(b) - total(a);
    });

  let maxShare = 0;
  for (const model of models) {
    for (const channel of model.channels) {
      if (channel.creditShare > maxShare) maxShare = channel.creditShare;
    }
  }

  return (
    <section className="card">
      <header>
        <h2>Credit share by model</h2>
        <span className="sub">
          {comparison.journeysAnalyzed} journeys analyzed
          {comparison.journeysSkipped > 0 ? ` · ${comparison.journeysSkipped} skipped (no touchpoints)` : ''}
        </span>
      </header>
      <div style={{ overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Channel</th>
              {models.map((m) => (
                <th key={m.model} style={{ textAlign: 'right' }}>{MODEL_LABELS[m.model]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {channelNames.map((channel) => (
              <tr key={channel}>
                <td style={{ fontWeight: 500 }}>{channel}</td>
                {models.map((m) => {
                  const row = m.channels.find((c) => c.channel === channel);
                  const share = row?.creditShare ?? 0;
                  return (
                    <td
                      key={m.model}
                      style={{
                        textAlign: 'right',
                        background: share > 0 && maxShare > 0
                          ? `color-mix(in srgb, var(--series-1) ${Math.round((share / maxShare) * 22)}%, transparent)`
                          : undefined,
                      }}
                    >
                      {share > 0 ? `${(share * 100).toFixed(1)}%` : '—'}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="muted" style={{ marginTop: 8 }}>
        Each column shows how that model splits 100% of attributed revenue across channels.
      </p>
    </section>
  );
}
